/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {decryptId} from '_metronic/helpers/Encrypt'
import {showErrorMessage} from '_metronic/helpers/swalHelper'
import {SetCampus} from '../../../../redux/user/userSlice'
import {useAppDispatch} from '../../../../redux/hooks'

const initialValues = {
  username: localStorage.getItem('username') || '',
  password: localStorage.getItem('password') ? decryptId(localStorage.getItem('password')) : '',
  rememberMe: false,
}

export function Login() {
  const {t} = useTranslation()
  const [showCampusModal, setShowCampusModal] = useState(false)
  const [rightCampuses, setRightCampuses] = useState<any>([])
  const [leftCampuses, setLeftCampuses] = useState<any>([])
  const dispatch = useAppDispatch()
  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.USERNAME')})),
    password: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.PASSWORD')})),
  })
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.username, values.password)
        saveAuth(auth)

        if (auth.permissions?.includes('access-to-all-campus-data')) {
          // Temporarily save campuses and token
          setRightCampuses(auth?.right_campuses)
          setLeftCampuses(auth?.left_campuses)
          setShowCampusModal(true)
          setLoading(false)
          return
        }

        // Otherwise, normal user -> fetch user and redirect
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
      } catch (error: any) {
        if (error.response?.data) {
          if (error.response.status === 404) {
            setStatus(t('MESSAGES.NOT_FOUND_ERROR'))
          } else {
            setStatus(error.response.data.message)
          }
        } else {
          console.log('Error', error.message)
        }
        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleCampusSelect = async (campusId: any) => {
    const formData = new FormData()
    formData.append('campus_id', campusId)
    dispatch(SetCampus(formData)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setCurrentUser(res.user)
        setShowCampusModal(false)
        window.location.href = '/dashboard'
      } else if (res.meta.requestStatus === 'rejected') {
        showErrorMessage(t('MESSAGES.SERVER_ERROR'))
      }
    })
  }

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        <div className='text-center'>
          <h1 className='text-dark fw-bolder my-4'>{t('GLOBAL.LOGIN_PAGE')}</h1>
        </div>

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : (
          <div></div>
        )}

        <div className='fv-row mb-3'>
          <label className='form-label fs-6 fw-bolder text-dark py-2'>
            {t('GLOBAL.USERNAME')}:
          </label>
          <input
            placeholder={t('GLOBAL.USERNAME')}
            {...formik.getFieldProps('username')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.username && formik.errors.username},
              {
                'is-valid': formik.touched.username && !formik.errors.username,
              }
            )}
            type='string'
            name='username'
            autoComplete='off'
          />
          {formik.touched.username && formik.errors.username && (
            <div className='fv-plugins-message-container'>
              <span role='alert' className='text-danger fw-bold'>
                {formik.errors.username}
              </span>
            </div>
          )}
        </div>

        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-dark fs-6 py-2'>
            {t('GLOBAL.PASSWORD')}:
          </label>
          <input
            type='password'
            placeholder={t('GLOBAL.PASSWORD')}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger fw-bold'>
                  {formik.errors.password}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className='d-flex justify-content-between'>
          <label className='form-check form-check-inline form-check-solid m-0'>
            <input
              type='checkbox'
              className='form-check-input mx-2'
              name='rememberMe'
              checked={formik.values.rememberMe}
              onChange={formik.handleChange}
            />
            {t('GLOBAL.REMEMBER_ME')}
          </label>
          <Link to='/forgot-password'>{t('GLOBAL.FORGOT_PASSWORD')}</Link>
        </div>

        <div className='d-grid py-4'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>{t('GLOBAL.LOGIN')}</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {t('GLOBAL.PLEASE_WAIT')}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        <div className='d-flex justify-content-center'>
          <a
            href='https://youtu.be/AALLzu7xIMs'
            className='fw-bold'
            target='_blank'
            rel='noopener noreferrer'
          >
            <span className='fa fa-video-camera px-1'></span>
            {t('GLOBAL.HOW_TO_PLATFORM')}
          </a>
        </div>
      </form>

      {showCampusModal && (
        <div
          className='modal show fade d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered modal-xl'>
            <div className='modal-content'>
              <div className='modal-header p-3 bg-primary'>
                <h5 className='modal-title text-white'>{t('Select Campus')}</h5>
              </div>
              <div className='modal-body'>
                <div className='row'>
                  {/* Left Campuses */}
                  <div className='col-md-6'>
                    <div className='row g-3'>
                      {rightCampuses.map((campus: any) => (
                        <div key={campus.id} className='col-12'>
                          <div
                            className='card border-success text-center cursor-pointer'
                            onClick={() => handleCampusSelect(campus.id)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = '#f8f9fa')
                            }
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div className='card-body p-2'>
                              <h6 className='card-title mb-0'>{campus.name}</h6>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Right Campuses */}
                  <div className='col-md-6'>
                    <div className='row g-3'>
                      {leftCampuses?.map((campus: any) => (
                        <div key={campus.id} className='col-12'>
                          <div
                            className='card border-primary text-center cursor-pointer'
                            onClick={() => handleCampusSelect(campus.id)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = '#f8f9fa')
                            }
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div className='card-body p-2'>
                              <h6 className='card-title mb-0'>{campus.name}</h6>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
